import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import { defaultInputDateFormat } from 'assets/utils/data/Date';
import { HttpStatus } from 'config/Api.Config';
import { set } from 'lodash';
import ContractCancellationReasons from 'models/enums/ContractCancellationReasons';
import ContractCancellationTypes from 'models/enums/ContractCancellationTypes';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import contractSetStatusApi from 'models/sales/contract/setStatus/Api.Contract.SetStatus';
import contractCancellationSaveApi from 'models/sales/contractCancellation/save/Api.ContractCancellation.Save';
import { SalesContractAction, useSalesContractProvider } from '../../Sales.Contract.Hooks';

export type ContractVoidCancellationForm = Api.Core.ContractCancellation.Save.IRequest &
  Api.Core.Contract.SetStatus.IRequest;
export default function ContractVoidCancellation() {
  const { lang } = getLocales();
  const { selected, listApi, pageRouter, setIsStatusChanging } = useSalesContractProvider();

  return (
    <ModalForm<ContractVoidCancellationForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected]}
      title={lang.contractCancellation}
      default={{
        newStatus: ContractStatusCode.CANCELED_VOID,
      }}
      onCancel={() => pageRouter.updateParams({ id: selected.id.toString(), action: SalesContractAction.INFO })}
      onSubmit={async ({ newStatus, ...data }) => {
        setIsStatusChanging(true);

        const contractStatusRes = await contractSetStatusApi(parseInt(pageRouter.params.id), {
          newStatus,
          triggerStatusFlow: true,
        });
        if (contractStatusRes.status !== HttpStatus.ok) {
          setIsStatusChanging(false);
          addLog({ error: contractStatusRes.message });
          return;
        }

        const contractCancellationRes = await contractCancellationSaveApi({
          ...data,
          type: ContractCancellationTypes.Void,
          contract: { id: contractStatusRes.payload, statusCode: newStatus },
          contractId: contractStatusRes.payload,
        });
        if (contractCancellationRes.status !== HttpStatus.ok) {
          setIsStatusChanging(false);
          addLog({ error: contractCancellationRes.message });
          return;
        }

        addLog({ success: lang.saveSuccess });
        pageRouter.updateParams();
        setIsStatusChanging(false);
        listApi.execute((b) => b);
      }}
      validation={(data, errors) => {
        if (!data?.cancellationDateLocal) set(errors, 'cancellationDateLocal', lang.mustNotBeEmpty);
        if (!data?.reason) set(errors, 'reason', lang.mustNotBeEmpty);
        if (data?.reason && data?.reason === ContractCancellationReasons.OTHER && !data?.reasonDescription)
          set(errors, 'reasonDescription', lang.mustNotBeEmpty);
      }}
      render={({ form }) => {
        return (
          <FormWrapper>
            <DateInput
              name="cancellationDateLocal"
              label={lang.cancellationDate}
              dateTimeFormat={defaultInputDateFormat}
              isLocal={true}
              value={form?.data?.cancellationDateLocal}
              error={form?.errors?.cancellationDateLocal}
              onChange={form.update}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <SelectInput
              name="reason"
              label={lang.cancellationReason}
              value={form?.data?.reason}
              error={form?.errors?.reason}
              data={Object.values(ContractCancellationReasons).map((id) => ({
                id,
                title: id,
              }))}
              onChange={form.update}
            />
            {form.data?.reason === ContractCancellationReasons.OTHER && (
              <MultilineTextInput
                name="reasonDescription"
                label={lang.cancellationReasonDescription}
                value={form?.data?.reasonDescription}
                error={form?.errors?.reasonDescription}
                onChange={form.update}
              />
            )}
            <NumberInput
              name="odometerReading"
              label={lang.odometerReading}
              value={form?.data?.odometerReading}
              error={form?.errors?.odometerReading}
              onChange={form.update}
            />
          </FormWrapper>
        );
      }}
    />
  );
}
